/**
 * Netly Pay settings: payment methods, approval rules, reminders, staff and
 * the join form.
 *
 * Pure, so the rules that have teeth — at least one payment method, the
 * reminder schedule, the SMS length, the field cycle — can be tested without
 * rendering. Spec: DESIGN-BRIEF.md §J and `vSettings()` in
 * docs/design/netly-pay/prototype.html.
 */
import {fmtDate, money} from '../../internals/managers/NetlyPayManager';

export type SettingsSection = 'methods' | 'rules' | 'reminders' | 'staff' | 'join';

export const SETTINGS_NAV: {key: SettingsSection; label: string}[] = [
  {key: 'methods', label: 'Payment methods'},
  {key: 'rules', label: 'Approval rules'},
  {key: 'reminders', label: 'Reminders'},
  {key: 'staff', label: 'Staff and roles'},
  {key: 'join', label: 'Join form and QR'},
];

export interface BankDetails {
  bank: string;
  branch: string;
  name: string;
  number: string;
}

export interface NetlyPaySettings {
  bank: BankDetails;
  bankOn: boolean;
  lankaqr: boolean;
  cash: boolean;
  autoInvite: boolean;
  proofTransfers: boolean;
  dupCheck: boolean;
  remindBefore: number;
  remindAfter: number[];
  remindChannel: 'App only' | 'App and SMS';
  lateFeeDefaultMinor: number;
  joinFields?: Record<string, JoinFieldState>;
}

export const DEFAULT_SETTINGS: NetlyPaySettings = {
  bank: {bank: '', branch: '', name: '', number: ''},
  bankOn: false,
  lankaqr: false,
  cash: true,
  autoInvite: false,
  proofTransfers: true,
  dupCheck: true,
  remindBefore: 3,
  remindAfter: [1, 7, 14],
  remindChannel: 'App and SMS',
  lateFeeDefaultMinor: 50000,
};

export const BANK_FIELDS: {key: keyof BankDetails; label: string; mono: boolean}[] = [
  {key: 'bank', label: 'Bank', mono: false},
  {key: 'branch', label: 'Branch', mono: false},
  {key: 'name', label: 'Account name', mono: false},
  {key: 'number', label: 'Account number', mono: true},
];

/**
 * Why a member could not be asked to pay.
 *
 * At least one method must be on — the same rule the Enable wizard enforces
 * on step 3. Turning the last one off here would leave requests nobody can
 * pay, so it is blocked rather than warned about. Bank transfer counts only
 * once the account is filled in: a method with no account number is not a way
 * to be paid.
 */
export function paymentMethodsError(s: NetlyPaySettings): string | null {
  const bankReady = s.bankOn && !!s.bank.name.trim() && !!s.bank.number.trim();
  if (s.bankOn && !bankReady) return 'Add the account name and number for bank transfers.';
  if (!bankReady && !s.lankaqr && !s.cash) return 'Keep at least one payment method on.';
  return null;
}

export const APPROVAL_RULES: {key: 'autoInvite' | 'proofTransfers' | 'dupCheck'; title: string; text: string}[] = [
  {
    key: 'autoInvite',
    title: 'Auto-approve members who join from the invite link',
    text: 'Off means new joins wait as Pending approval until staff approve them.',
  },
  {
    key: 'proofTransfers',
    title: 'Require proof for bank transfers',
    text: 'Members must upload a slip or screenshot. Payments without proof are not accepted.',
  },
  {
    key: 'dupCheck',
    title: 'Flag possible duplicate payments',
    text: 'Marks proofs with the same reference or amount for the same request in Approvals.',
  },
];

// ── Reminders ───────────────────────────────────────────────────────────────

export const REMIND_BEFORE = [1, 3, 5, 7];
export const REMIND_AFTER = [1, 3, 7, 14, 30];

const days = (n: number) => `${n} ${n === 1 ? 'day' : 'days'}`;

export function beforeLabel(n: number): string {
  return `${days(n)} before`;
}

export function afterLabel(n: number, on: boolean): string {
  return `${on ? '✓ ' : ''}${days(n)} after`;
}

/** Add or drop one "after" reminder, kept in order so the schedule reads right. */
export function toggleAfter(list: number[], n: number): number[] {
  return list.includes(n) ? list.filter((x) => x !== n) : list.concat([n]).sort((a, b) => a - b);
}

/**
 * The reminder schedule in one sentence.
 *
 * Reads as a plan rather than as chips, so an owner can tell at a glance that
 * they are about to message a parent five times about one fee.
 */
export function reminderPlan(s: NetlyPaySettings): string {
  const after = s.remindAfter.length
    ? `then ${s.remindAfter.map((n) => days(n)).join(', ')} after it`
    : 'and nothing after it';
  return `A reminder ${days(s.remindBefore)} before the due date, ${after}. Sent by ${s.remindChannel.toLowerCase()}.`;
}

/** One SMS is 160 characters; longer messages are billed and sent as parts. */
export const SMS_LIMIT = 160;

export interface SmsPreview {
  text: string;
  length: number;
  parts: number;
  /** True once it costs more than one message. */
  overLimit: boolean;
  note: string;
}

/**
 * The reminder as the parent will receive it.
 *
 * The character count is part of the preview because SMS is billed per 160
 * characters, and a shop name long enough to push a routine reminder onto a
 * second message doubles the bill for every reminder it ever sends.
 */
export function smsPreview(
  shopName: string,
  request: {title: string; amountMinor: number; due: string},
  currency = 'Rs.',
): SmsPreview {
  const text = `${shopName}: ${request.title} ${money(request.amountMinor, currency)} is due on ${fmtDate(
    request.due,
  )}. Pay from the Netly Pay app.`;
  const length = text.length;
  const parts = Math.max(1, Math.ceil(length / SMS_LIMIT));
  return {
    text,
    length,
    parts,
    overLimit: length > SMS_LIMIT,
    note:
      length > SMS_LIMIT
        ? `${length} characters · sent as ${parts} messages`
        : `${length} of ${SMS_LIMIT} characters`,
  };
}

// ── Staff and roles ─────────────────────────────────────────────────────────

export type StaffRole = 'Owner' | 'Front desk' | 'Coach' | 'None';

export interface Staff {
  id?: string;
  name: string;
  email: string;
  role: StaffRole;
  invited?: boolean;
}

export const ROLE_CARDS: {title: string; text: string}[] = [
  {title: 'Owner', text: 'Everything, including payment methods, approvals rules and staff.'},
  {title: 'Front desk', text: 'Members, requests, approvals and cash. No settings or exports.'},
  {
    title: 'Coach',
    text: 'Marks attendance for their batches in the Netly Pay app. Sees rosters, not balances.',
  },
];

export interface StaffRow {
  id: string;
  name: string;
  email: string;
  role: StaffRole;
  /** The roles this row may be switched to. An owner cannot be demoted here. */
  options: StaffRole[];
  locked: boolean;
}

/**
 * The staff table.
 *
 * The owner's row is locked: a shop with no owner has nobody who can set the
 * payment methods or approve anything, and this screen is reachable by the
 * owner themselves, so the one mistake to prevent is demoting yourself.
 */
export function staffRows(staff: Staff[]): StaffRow[] {
  return (staff ?? []).map((p, i) => ({
    id: p.id ?? p.email ?? String(i),
    name: `${p.name}${p.invited ? ' · invited' : ''}`,
    email: p.email,
    role: p.role,
    options: p.role === 'Owner' ? ['Owner'] : ['Coach', 'Front desk', 'None'],
    locked: p.role === 'Owner',
  }));
}

/** Why this invite cannot be sent. Null when it can. */
export function inviteError(email: string, staff: Staff[] = []): string | null {
  const value = String(email ?? '').trim();
  if (!value) return 'Enter an email address';
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return 'Enter an email address';
  if ((staff ?? []).some((p) => p.email.toLowerCase() === value.toLowerCase())) {
    return 'That person already has access';
  }
  return null;
}

// ── Join form ───────────────────────────────────────────────────────────────

export type JoinFieldState = 'Required' | 'Optional' | 'Hidden';

export interface JoinField {
  key: string;
  label: string;
  sub: string;
  /** Name and phone are always required — a member without either is unusable. */
  locked: boolean;
}

export const JOIN_FIELDS: JoinField[] = [
  {key: 'name', label: 'Full name', sub: 'Always required', locked: true},
  {key: 'phone', label: 'Phone', sub: 'Always required. Requests are sent here.', locked: true},
  {key: 'dob', label: 'Date of birth', sub: 'Used for age groups', locked: false},
  {key: 'batch', label: 'Program and batch', sub: 'Member picks from open batches', locked: false},
  {key: 'guardian', label: 'Guardian name and phone', sub: 'For members under 18', locked: false},
  {key: 'email', label: 'Email', sub: '', locked: false},
  {key: 'jersey', label: 'Jersey size', sub: 'Custom field', locked: false},
  {key: 'school', label: 'School', sub: 'Custom field', locked: false},
  {key: 'medical', label: 'Medical notes', sub: 'Custom field · shown to coaches', locked: false},
];

export const DEFAULT_JOIN_FIELDS: Record<string, JoinFieldState> = {
  dob: 'Required',
  batch: 'Required',
  guardian: 'Required',
  email: 'Optional',
  jersey: 'Optional',
  school: 'Optional',
  medical: 'Optional',
};

/** Required → Optional → Hidden → Required. One control, three states. */
export function cycleJoinField(state: JoinFieldState): JoinFieldState {
  if (state === 'Required') return 'Optional';
  if (state === 'Optional') return 'Hidden';
  return 'Required';
}

export interface JoinFieldRow extends JoinField {
  state: JoinFieldState;
  tone: 'accent' | 'default' | 'muted';
}

export function joinFieldRows(config: Record<string, JoinFieldState> = {}): JoinFieldRow[] {
  return JOIN_FIELDS.map((f) => {
    const state: JoinFieldState = f.locked ? 'Required' : config[f.key] ?? DEFAULT_JOIN_FIELDS[f.key] ?? 'Optional';
    return {
      ...f,
      state,
      tone: state === 'Required' ? 'accent' : state === 'Hidden' ? 'muted' : 'default',
    };
  });
}

/** The link on the poster, and what Copy link copies. */
export function joinLink(workspaceId: string, origin = 'https://netly.netlise.com'): string {
  return `${origin}/join/${encodeURIComponent(workspaceId)}`;
}

/** What the poster says under the QR code. */
export function posterCaption(shopName: string): string {
  return `Scan to join ${shopName} on Netly Pay`;
}
